// ---------------------------------------------------------------------------
// Busca fuzzy com ranking por relevância e peso IDF
//
// Compatível com dois contextos:
//  - @qcx/olaf (ILS/Svelte): options têm { label, value, ... }
//  - qselect (odin/jQuery): options podem ter campos variáveis (nome, title…)
//
// Use o parâmetro `getText` para adaptar ao formato da opção:
//   fuzzySearch(options, query)                           → usa option.label
//   fuzzySearch(options, query, opt => opt[textField])   → campo customizado
// ---------------------------------------------------------------------------

const FUZZY_CONFIG = {
  scores: {
    exactMatch: 1000, // frase relevante idêntica
    startsWith: 500, // cargo começa com a busca
    contains: 300, // cargo contém a frase buscada
    tokenMatch: 50, // cada token encontrado (match exato) × peso IDF
    prefixMatch: 25, // token do cargo começa com o prefixo buscado × peso IDF
    tokenOrderBonus: 20, // bônus por token quando a ordem está correta
    sizeDiffPenalty: 20 // penalidade por palavra a mais no cargo vs busca
  },
  prefixMinLength: 5, // mínimo de chars para ativar prefix match
  maxIdfWeight: 6, // limite superior do peso IDF
  maxResults: 50
};

// Palavras que não agregam valor semântico à comparação
const STOPWORDS = new Set([
  'de',
  'do',
  'da',
  'dos',
  'das',
  'em',
  'no',
  'na',
  'nos',
  'nas',
  'para',
  'pelo',
  'pela',
  'pelos',
  'pelas',
  'com',
  'por',
  'e',
  'ou',
  'a',
  'o',
  'as',
  'os',
  'um',
  'uma',
  'uns',
  'umas',
  'ao',
  'aos',
  'à',
  'às'
]);

// ---------------------------------------------------------------------------
// Normalização e tokenização
// ---------------------------------------------------------------------------

function normalizeText(text) {
  return text
    .toLowerCase()
    .replace(/[àáâãÀÁÂÃ]/g, 'a')
    .replace(/[èéêÈÉÊ]/g, 'e')
    .replace(/[ìíÌÍ]/g, 'i')
    .replace(/[òóôÒÓÔ]/g, 'o')
    .replace(/[ùúüÙÚÜ]/g, 'u')
    .replace(/[çÇ]/g, 'c')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalizeText(text)
    .split(' ')
    .filter(token => token.length > 0 && !STOPWORDS.has(token));
}

function toRelevantPhrase(text) {
  return tokenize(text).join(' ');
}

// ---------------------------------------------------------------------------
// Indexação com IDF
//
// WeakMap cacheia o índice por referência do array:
//  - olaf: state.data é estável após oneTimeFetch → índice construído uma vez
//  - qselect: this.options é estável após setOptions → mesma garantia
// ---------------------------------------------------------------------------

const indexCache = new WeakMap();

function getIndex(options, getText) {
  if (indexCache.has(options)) {
    return indexCache.get(options);
  }

  const index = options.map(option => {
    const text = getText(option);
    return {
      option,
      tokens: tokenize(text),
      relevant: toRelevantPhrase(text)
    };
  });

  // Frequência de documentos por token
  const docFreq = {};
  index.forEach(item => {
    new Set(item.tokens).forEach(token => {
      docFreq[token] = (docFreq[token] || 0) + 1;
    });
  });

  const result = { index, docFreq };
  indexCache.set(options, result);
  return result;
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

function scoreOption(indexedOption, weightedQueryTokens, queryRelevant) {
  const { tokens, relevant } = indexedOption;

  if (tokens.length === 0 || weightedQueryTokens.length === 0) {
    return 0;
  }

  // Match exato na frase relevante → maior prioridade
  if (relevant === queryRelevant) {
    return FUZZY_CONFIG.scores.exactMatch;
  }

  let score = 0;

  if (relevant.startsWith(queryRelevant)) {
    score += FUZZY_CONFIG.scores.startsWith;
  } else if (relevant.includes(queryRelevant)) {
    score += FUZZY_CONFIG.scores.contains;
  }

  let matchedCount = 0;
  let lastMatchIndex = -1;
  let inOrder = true;

  for (const { token: qt, idfWeight } of weightedQueryTokens) {
    // 1. Match exato
    let matchIdx = tokens.indexOf(qt);
    let isExact = matchIdx > -1;

    // 2. Prefix match — cargo tem token que começa com os primeiros N chars da busca
    if (!isExact && qt.length >= FUZZY_CONFIG.prefixMinLength) {
      const prefix = qt.slice(0, FUZZY_CONFIG.prefixMinLength);
      matchIdx = tokens.findIndex(t => t.startsWith(prefix));
    }

    if (matchIdx > -1) {
      matchedCount++;
      const baseScore = isExact
        ? FUZZY_CONFIG.scores.tokenMatch
        : FUZZY_CONFIG.scores.prefixMatch;
      // Tokens raros (específicos do domínio) valem mais que tokens genéricos
      score += baseScore * idfWeight;

      if (matchIdx < lastMatchIndex) {
        inOrder = false;
      }
      lastMatchIndex = matchIdx;
    }
  }

  if (matchedCount === 0) {
    return 0;
  }

  if (inOrder && matchedCount > 1) {
    score += FUZZY_CONFIG.scores.tokenOrderBonus * matchedCount;
  }

  const sizeDiff = Math.abs(tokens.length - weightedQueryTokens.length);
  score -= sizeDiff * FUZZY_CONFIG.scores.sizeDiffPenalty;

  return Math.max(score, 1);
}

// ---------------------------------------------------------------------------
// Função principal
//
// @param {Array}    options  - array de opções a filtrar
// @param {string}   query    - texto digitado pelo usuário
// @param {Function} getText  - extrai o texto de cada opção (padrão: opt.label)
//
// Compatível com a assinatura do customMethod do @qcx/olaf:
//   search: { customMethod: fuzzySearch }
//
// No qselect, passe getText:
//   fuzzySearch(this.options, rawText, opt => opt[this.textField] || opt.toString())
// ---------------------------------------------------------------------------

export function fuzzySearch(options, query, getText = opt => opt.label || '') {
  if (!query || !query.trim()) {
    return options;
  }

  const queryTokens = tokenize(query);
  const queryRelevant = queryTokens.join(' ');

  if (queryTokens.length === 0) {
    return [];
  }

  const { index, docFreq } = getIndex(options, getText);
  const N = options.length;

  // Peso IDF por token: log((N+1) / df)
  // Tokens genéricos (aparecem em muitos cargos) → peso baixo
  // Tokens específicos (aparecem em poucos cargos) → peso alto
  const weightedQueryTokens = queryTokens.map(token => {
    const df = docFreq[token] || 0;
    const idfWeight =
      df > 0
        ? Math.min(Math.log((N + 1) / df), FUZZY_CONFIG.maxIdfWeight)
        : 1.0;
    return { token, idfWeight };
  });

  return index
    .map(indexed => ({
      option: indexed.option,
      score: scoreOption(indexed, weightedQueryTokens, queryRelevant)
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, FUZZY_CONFIG.maxResults)
    .map(item => item.option);
}
